import React, { useState, useEffect } from 'react';
import { adminFetch } from '../lib/api';
import { FileText, RefreshCw, Loader2, GitBranch, Tag } from 'lucide-react';

export const ChangelogPage: React.FC = () => {
  const [changelog, setChangelog] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadChangelog = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminFetch('/api/admin/changelog');
      const data = await resp.json();
      setChangelog(data.changelog || 'Kein Changelog verfügbar.');
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChangelog(); }, []);

  // Parse changelog into structured sections
  const sections = changelog
    .split(/^## /m)
    .filter(s => s.trim())
    .map(section => {
      const lines = section.split('\n');
      const title = lines[0]?.trim() || '';
      const body = lines.slice(1).join('\n').trim();
      
      // Extract version and date from title like "[1.50.0](url) (2026-04-01)"
      const versionMatch = title.match(/\[?([\d.]+)\]?/);
      const dateMatch = title.match(/\((\d{4}-\d{2}-\d{2})\)/);
      
      // Categorize changes
      const features = (body.match(/\*\*Features?\*\*[\s\S]*?(?=\*\*|$)/gi) || []).join('\n');
      const fixes = (body.match(/\*\*Bug Fixes?\*\*[\s\S]*?(?=\*\*|$)/gi) || []).join('\n');
      const other = body
        .replace(/\*\*Features?\*\*[\s\S]*?(?=\*\*|$)/gi, '')
        .replace(/\*\*Bug Fixes?\*\*[\s\S]*?(?=\*\*|$)/gi, '')
        .trim();

      return {
        version: versionMatch?.[1] || title,
        date: dateMatch?.[1] || '',
        rawTitle: title,
        features,
        fixes,
        other,
        body,
      };
    });

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FileText size={24} style={{ color: '#E03705' }} />
          <h1>Changelog</h1>
        </div>
        <button className="btn btn-secondary" onClick={loadChangelog} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginTop: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Loader2 size={32} className="animate-spin" style={{ color: '#E03705' }} />
        </div>
      ) : sections.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <FileText size={48} style={{ color: '#999', marginBottom: 12 }} />
          <p style={{ color: '#666' }}>Kein Changelog verfügbar.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          {sections.map((sec, i) => (
            <div key={i} className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{
                  background: i === 0 ? '#E03705' : '#666',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <Tag size={14} />
                  v{sec.version}
                </div>
                {sec.date && (
                  <span style={{ color: '#888', fontSize: 13 }}>{sec.date}</span>
                )}
                {i === 0 && (
                  <span style={{
                    background: '#dcfce7',
                    color: '#16a34a',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    AKTUELL
                  </span>
                )}
              </div>

              {sec.features && (
                <div style={{ marginBottom: 12 }}>
                  <h4 style={{ color: '#16a34a', fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    ✨ Features
                  </h4>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: sec.features.replace(/\*\*Features?\*\*/gi, '').replace(/\* /g, '• ').replace(/\n/g, '<br/>') }}
                  />
                </div>
              )}

              {sec.fixes && (
                <div style={{ marginBottom: 12 }}>
                  <h4 style={{ color: '#dc2626', fontSize: 13, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    🐛 Bug Fixes
                  </h4>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: sec.fixes.replace(/\*\*Bug Fixes?\*\*/gi, '').replace(/\* /g, '• ').replace(/\n/g, '<br/>') }}
                  />
                </div>
              )}

              {sec.other && !sec.features && !sec.fixes && (
                <div style={{ fontSize: 13, lineHeight: 1.6, color: '#555', whiteSpace: 'pre-wrap' }}>
                  {sec.body.replace(/\* /g, '• ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
